import React, { FC, useMemo, useCallback, useEffect } from 'react';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { createContext } from '@shared/utils/createContext';
import { PageSettingsDialog } from '@shared/components/PageSettingsDialog/PageSettingsDialog';
import { VisibilityConfig } from '@shared/components/PageSettingsDialog/PageSettingsDialog.types';
import { CheckFilled } from '@withjoy/joykit/icons';
import { useDisclosure } from '@withjoy/joykit';
import { EventPageVisibility, useGetVisibilityConfigLazyQuery, useUpdateEventPasswordMutation, useUpdatePageVisibilityMutation } from '@graphql/generated';
import { formatVisibilityConfig, getVisibilityTranslation } from './PageSettingsDialog.utils';
import { FocusPointEditorTelemetryProvider } from '@shared/components/FocusPointEditor/FocusPointEditor.telemetry';

export type UpdatedData = {
  visibility: EventPageVisibility;
  visibilityChanged: boolean;
  eventPasswordChanged: boolean;
};

type PageSettingsDialogProviderContext = {
  isOpen: boolean;
  openPageSettingsDialog: () => void;
  closePageSettingsDialog: () => void;
  visibilityConfig?: VisibilityConfig;
};

const [Provider, usePageSettingsDialogContext] = createContext<PageSettingsDialogProviderContext>({ name: 'PageSettingsDialogProviderContext' });

interface PageSettingsDialogProviderProps {
  eventId?: string;
  eventPageId?: string;
  resource: string;
  onDialogOpen?: () => void;
  onDialogClose?: () => void;
  onDialogUpdate?: ({ visibility, visibilityChanged, eventPasswordChanged }: UpdatedData) => void;
}

const PageSettingsDialogProvider: FC<PageSettingsDialogProviderProps> = ({ children, eventId, eventPageId, resource, onDialogOpen, onDialogClose, onDialogUpdate }) => {
  const { t } = useTranslation('yourRegistry');
  const translations = t('visibilityDialog');
  const { toast } = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [fetchVisibilityData, { loading: fetchingVisibilityConfig, data: visibilityData }] = useGetVisibilityConfigLazyQuery({
    batchMode: 'fast',
    fetchPolicy: 'cache-first'
  });

  useEffect(() => {
    if (eventId && eventPageId) {
      fetchVisibilityData({ variables: { eventId, eventPageId } });
    }
  }, [eventId, eventPageId, fetchVisibilityData]);

  const [updatePageVisibility, { loading: mutatingVisibilityConfig }] = useUpdatePageVisibilityMutation();
  const [updateEventPassword, { loading: changingPassword }] = useUpdateEventPasswordMutation();
  const visibilityConfig = useMemo(() => formatVisibilityConfig(visibilityData), [visibilityData]);
  const isLoading = fetchingVisibilityConfig || mutatingVisibilityConfig || changingPassword;

  const handleOpen = useCallback(() => {
    onOpen();
    onDialogOpen && onDialogOpen();
  }, [onOpen, onDialogOpen]);

  const handleClose = useCallback(() => {
    onClose();
    onDialogClose && onDialogClose();
  }, [onClose, onDialogClose]);

  const onSave = useCallback(
    async ({ visibility, password }: VisibilityConfig) => {
      if (!eventId || !eventPageId) {
        return;
      }
      const shouldUpdatePassword = visibility === EventPageVisibility.passwordProtected && password !== visibilityConfig?.password;
      const shouldUpdateVisibility = visibility !== visibilityConfig?.visibility;
      const asyncTasks = [];
      if (shouldUpdatePassword) {
        asyncTasks.push(
          updateEventPassword({
            variables: {
              eventId,
              eventPassword: password
            },
            refetchQueries: ['GetVisibilityConfig']
          })
            .then(() => toast(translations.changePasswordSuccessful(), { icon: <CheckFilled /> }))
            .catch(e => toast(e.message || translations.changePasswordFailed({ resource })))
        );
      }
      if (shouldUpdateVisibility) {
        asyncTasks.push(
          updatePageVisibility({
            variables: {
              id: eventPageId,
              payload: {
                disabled: visibility === EventPageVisibility.hidden,
                private: visibility === EventPageVisibility.passwordProtected
              }
            }
          })
            .then(() =>
              toast(translations.changeVisibilitySuccessful({ resource, visibility: getVisibilityTranslation(translations, visibility) }), {
                icon: <CheckFilled />
              })
            )
            .catch(() => toast(translations.changeVisibilityFailed({ resource })))
        );
      }

      Promise.allSettled(asyncTasks).then(() => {
        onDialogUpdate && onDialogUpdate({ visibility, visibilityChanged: shouldUpdateVisibility, eventPasswordChanged: shouldUpdatePassword });
        handleClose();
      });
    },
    [eventId, eventPageId, handleClose, updateEventPassword, updatePageVisibility, resource, translations, toast, visibilityConfig, onDialogUpdate]
  );
  return (
    <Provider
      value={{
        isOpen,
        closePageSettingsDialog: handleClose,
        openPageSettingsDialog: handleOpen,
        visibilityConfig
      }}
    >
      {children}
      <FocusPointEditorTelemetryProvider>
        <PageSettingsDialog
          isOpen={isOpen}
          onClose={handleClose}
          onSave={onSave}
          isLoading={isLoading}
          pageVisibility={{ initialVisibility: visibilityConfig?.visibility ?? EventPageVisibility.passwordProtected, initialPassword: visibilityConfig?.password }}
          pageDisplayName={`${resource} Settings`}
          telemetryCategory="registry"
        />
      </FocusPointEditorTelemetryProvider>
    </Provider>
  );
};

PageSettingsDialogProvider.displayName = 'PageSettingsDialogProvider';

export { PageSettingsDialogProvider, usePageSettingsDialogContext };
